import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchDistricts,
  fetchLogisticLines,
  searchProducts,
  fetchCustomerCateogries,
  addLogistics,
  updateLogisticsOrder,
  fetchPaymentMethods,
} from '../services/ordersService';
import BasicTimePicker from '../../../components/BasicTimePicker';
import { showSuccessToast } from '../../../utils/toastHelper';
import { AsyncPaginate } from 'react-select-async-paginate';
import useDynamicInputWidth from '../hooks/useDynamicInputWidth';
import { trimDecimalZero } from '../../../utils/commonUtils';

const LogisticsOrderModal = ({
  setAddEditModal,
  onSuccess,
  logisticsOrder = null,
}) => {
  const { t } = useTranslation('Order');
  const [loader, setLoader] = useState(false);

  const [referenceNo, setReferenceNo] = useState('');
  const [customerCategory, setCustomerCategory] = useState('');

  const [districtId, setDistrictId] = useState(''); // Store district ID
  const [logisticLineId, setLogisticLineId] = useState(''); // Store logistic line ID

  const [deliveryDate, setDeliveryDate] = useState('');
  const [earliestTimeFrom, setEarliestTimeFrom] = useState('');
  const [earliestTimeTo, setEarliestTimeTo] = useState('');
  const [latestTimeFrom, setLatestTimeFrom] = useState('');
  const [latestTimeTo, setLatestTimeTo] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [orderRemark, setOrderRemark] = useState('');

  const [productInfo, setProductsInfo] = useState([]);
  const [products, setProducts] = useState([]);
  const dynamicSizeInputRefs = useRef([]);
  const specificationInputRefs = useRef([]);
  const productRemarkInputRefs = useRef([]);
  const [paymentMethodId, setPaymentMethodId] = useState('');
  useDynamicInputWidth(dynamicSizeInputRefs, products);
  useDynamicInputWidth(
    specificationInputRefs,
    products,
    (item) => item.specification
  );
  useDynamicInputWidth(
    productRemarkInputRefs,
    products,
    (item) => item.productRemark
  );
  useEffect(() => {
    getCustomerCategory();
    getDistrict();
    getLogisticLine();
    getPaymentMethods();

    if (logisticsOrder) {
      setReferenceNo(logisticsOrder.reference_number || '');
      setCustomerCategory(logisticsOrder.logistic_customer_category_id || '');
      setDistrictId(logisticsOrder.district_id || '');
      setLogisticLineId(logisticsOrder.logistic_line_id || '');
      setDeliveryDate(logisticsOrder.delivery_date.replace(/\//g, '-'));
      setPaymentMethodId(logisticsOrder.payment_method_id || '');

      const parseTimeRange = (timeRange) => {
        if (!timeRange) return ['', ''];
        const [from, to] = timeRange.split('-');
        return [from, to || ''];
      };

      const [etf, ett] = parseTimeRange(logisticsOrder.earliest_delivery_time);
      const [ltf, ltt] = parseTimeRange(logisticsOrder.latest_delivery_time);

      setEarliestTimeFrom(etf);
      setEarliestTimeTo(ett);
      setLatestTimeFrom(ltf);
      setLatestTimeTo(ltt);

      setContactPerson(logisticsOrder.contact_name || '');
      setContactPhone(logisticsOrder.contact_phone || '');
      setDeliveryAddress(logisticsOrder.delivery_address || '');
      setOrderRemark(logisticsOrder.remark || '');

      const mappedProducts =
        logisticsOrder.products?.map((product) => ({
          productId: product.product_id,
          productCode: product.product_code || '',
          productName: product.product_name || '',
          origin: product.origin || '',
          specification: product.specification || '',
          quantity: product.quantity,
          productRemark: product.remarks || '',
          processLocation: product.process_location || '',
          // isEditing: false,
        })) || [];

      setProducts(mappedProducts);
    }
  }, []);

  const handleSave = async () => {
    setLoader(true); // Start loading

    const simplifiedProducts = products.map((product) => ({
      product_id: product?.productId,
      quantity: product?.quantity,
      remarks: product?.productRemark,
    }));

    const formatTimeRange = (from, to) => {
      if (!from) return null; // If 'from' is not selected, return null
      return to ? `${from}-${to}` : from; // Include 'to' only if it's selected
    };
    const formData = {
      delivery_date: deliveryDate,
      reference_number: referenceNo,
      logistic_customer_category_id: customerCategory,
      district_id: districtId,
      logistic_line_id: logisticLineId,
      payment_method_id: paymentMethodId,
      earliest_delivery_time: formatTimeRange(earliestTimeFrom, earliestTimeTo),
      latest_delivery_time: formatTimeRange(latestTimeFrom, latestTimeTo),
      contact_name: contactPerson,
      contact_phone: contactPhone,
      delivery_address: deliveryAddress,
      remark: orderRemark,
      products: simplifiedProducts,
    };

    let response;
    if (logisticsOrder?.id) {
      response = await updateLogisticsOrder(formData, logisticsOrder.id); // You’ll need to create this API
    } else {
      response = await addLogistics(formData);
    }

    if (response?.success) {
      onSuccess();
      setAddEditModal(false);
      showSuccessToast(response.message);
    }

    setLoader(false);
  };

  // useEffect(() => {
  //   getCustomerCategory();
  //   getDistrict();
  //   getLogisticLine();
  // }, []);

  const [districtOptions, setDistricts] = useState([]);
  const [logisticLineOptions, setLogistic] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  const [categoryOptions, setCustomerCategorys] = useState([]);

  const getDistrict = async () => {
    try {
      const response = await fetchDistricts();
      setDistricts(response?.districts || []);
    } catch (error) {
      console.error('Error fetching districts:', error);
    }
  };

  const getLogisticLine = async () => {
    try {
      const response = await fetchLogisticLines();
      const logisticLines = response?.logistic_lines || [];
      setLogistic(logisticLines);
      const selectedLogistic = logisticLines.find(
        (item) => item.name === '天霖國際有限公司'
      );
      setLogisticLineId(selectedLogistic ? selectedLogistic.id : null);
    } catch (error) {
      console.error('Error fetching logistic lines:', error);
    }
  };
  const getPaymentMethods = async () => {
    try {
      const response = await fetchPaymentMethods();
      setPaymentMethods(response?.payment_methods || []);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
  };

  const getCustomerCategory = async () => {
    try {
      const response = await fetchCustomerCateogries();
      setCustomerCategorys(response?.customer_categories || []);
    } catch (error) {
      console.error('Error fetching customer categories:', error);
    }
  };

  const handleDistrictChange = (e) => {
    const selectedDistrictId = e.target.value;
    setDistrictId(selectedDistrictId); // Store district ID
  };

  const handleLogisticLineChange = (e) => {
    const selectedLogisticLineId = e.target.value;
    setLogisticLineId(selectedLogisticLineId); // Store logistic line ID
  };
  const handlePaymentMethodChange = (e) => {
    const selectedPaymentMethodId = e.target.value;
    setPaymentMethodId(selectedPaymentMethodId); // Store logistic line ID
  };

  const addProduct = () => {
    setProducts([
      ...products,
      {
        productCode: '',
        productName: '',
        origin: '',
        specification: '',
        quantity: '',
        productRemark: '',
        processLocation: '',
        productId: '',
        // isEditing: true,
      },
    ]);
  };

  const removeProduct = (index) => {
    const updatedProducts = products.filter((_, i) => i !== index);
    setProducts(updatedProducts);
  };
  const handleProductChange = async (index, eventOrSelectedOptions) => {
    const updatedProducts = [...products];

    if (eventOrSelectedOptions && eventOrSelectedOptions.target) {
      let { name, value } = eventOrSelectedOptions.target;
      if (name === 'quantity') {
        value = trimDecimalZero(value);
      }
      // Update the specific field in the product
      updatedProducts[index] = {
        ...updatedProducts[index],
        [name]: value,
      };
    } else {
      const selectedOptions = eventOrSelectedOptions;
      const selectedId = selectedOptions?.value || null;

      if (selectedId) {
        try {
          const matchedProduct = productInfo.find(
            (product) => product.id === selectedId
          );

          // Update the product state with the fetched details
          updatedProducts[index] = {
            ...updatedProducts[index],
            productId: matchedProduct?.id,
            productName: matchedProduct?.name || '',
            productCode: matchedProduct?.code || '',

            specification: matchedProduct?.specification || '',
            origin: matchedProduct.origin?.name || '',
            processLocation: matchedProduct?.process_location?.name || '',
          };
          setProducts(updatedProducts);
        } catch (error) {
          console.error('Error fetching product details:', error);
        }
      } else {
        updatedProducts[index] = {
          ...updatedProducts[index],
          productId: null,
          productName: '',
          productCode: '',
          specification: '',
          origin: '',
          processLocation: '',
        };
      }
    }
    setProducts(updatedProducts);
  };
  const productsLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const per_page = 50;
    const query = {
      page,
      per_page,
      search: inputValue,
    };

    const productsResponse = await searchProducts(query);

    setProductsInfo((prev) => [...prev, ...(productsResponse?.products || [])]);

    const newOptions = productsResponse.products?.map((product) => ({
      value: product.id,
      label: `${product.code} - ${product.name}`,
    }));
    return {
      options: newOptions,
      hasMore:
        productsResponse.pagination.current_page <
        productsResponse.pagination.last_page,
      additional: {
        page: page + 1,
      },
    };
  };
  return (
    <div className="modal fade show d-block add-new-fbz" id="add-order-modal">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            {logisticsOrder ? t('EDITLOGISTICORDER') : t('ADDNEWLOGISTICORDER')}
            <button
              type="button"
              className="btn-close"
              onClick={() => setAddEditModal(false)}
            ></button>
          </div>
          <div className="modal-body">
            <div className="input-main">
              <div className="row">
                {/* Delivery Date */}
                <div className="col-sm-4">
                  <div className="custom-floating-group">
                    <input
                      type="date"
                      name="deliveryDate"
                      value={deliveryDate}
                      onChange={(e) => setDeliveryDate(e.target.value)}
                    />
                    <label className="delivery-label">
                      {t('DELIVERYDATE')}
                    </label>
                  </div>
                </div>

                {/** Reference No */}
                <div className="col-sm-4">
                  <div className="custom-floating-group">
                    <input
                      type="text"
                      value={referenceNo}
                      onChange={(e) => setReferenceNo(e.target.value)}
                      disabled={logisticsOrder}
                    />
                    <label>{t('REFERENCENO')}</label>
                  </div>
                </div>

                {/** Customer Category */}
                <div className="col-sm-4">
                  <div className="custom-floating-group">
                    <select
                      value={customerCategory}
                      onChange={(e) => setCustomerCategory(e.target.value)}
                    >
                      <option value="">{t('SELECTCATEGORY')}</option>
                      {categoryOptions.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <label>{t('CUSTOMERCATEGORY')}</label>
                  </div>
                </div>

                {/** District */}
                <div className="col-sm-4">
                  <div className="custom-floating-group">
                    <select value={districtId} onChange={handleDistrictChange}>
                      <option value="">{t('SELECTDISTRICT')}</option>
                      {districtOptions.map((district) => (
                        <option key={district.id} value={district.id}>
                          {district.name}
                        </option>
                      ))}
                    </select>
                    <label>{t('DISTRICT')}</label>
                  </div>
                </div>

                {/** Logistic Line */}
                <div className="col-sm-4">
                  <div className="custom-floating-group">
                    <select
                      value={logisticLineId}
                      onChange={handleLogisticLineChange}
                    >
                      <option value="">{t('SELECTLOGISTICLINE')}</option>
                      {logisticLineOptions.map((line) => (
                        <option key={line.id} value={line.id}>
                          {line.name}
                        </option>
                      ))}
                    </select>
                    <label>{t('LOGISTICLINE')}</label>
                  </div>
                </div>
                <div className="col-sm-4">
                  <div className="custom-floating-group">
                    <select
                      value={paymentMethodId}
                      onChange={handlePaymentMethodChange}
                    >
                      <option value="">{t('SELECTPAYMENTMETHOD')}</option>
                      {paymentMethods.map((payment_method) => (
                        <option
                          key={payment_method.id}
                          value={payment_method.id}
                        >
                          {payment_method.name}
                        </option>
                      ))}
                    </select>
                    <label>{t('PAYMENTMETHOD')}</label>
                  </div>
                </div>

                {/** Delivery Times */}
                {[
                  t('EARLIESTTIMEFROM'),
                  t('EARLIESTTIMETO'),
                  t('LATESTTIMEFROM'),
                  t('LATESTTIMETO'),
                ].map((label, index) => {
                  const isToField = index === 1 || index === 3; // 1 (EarliestTo) and 3 (LatestTo)
                  const correspondingFromTime =
                    index === 1 ? earliestTimeFrom : latestTimeFrom; // Matching From time

                  return (
                    <div key={index} className="col-sm-4">
                      <div className="custom-floating-group">
                        <BasicTimePicker
                          name="end_time"
                          value={
                            [
                              earliestTimeFrom,
                              earliestTimeTo,
                              latestTimeFrom,
                              latestTimeTo,
                            ][index]
                          }
                          disabled={isToField && !correspondingFromTime}
                          placeholder={label}
                          onChange={(time, name) => {
                            if (!time) return;
                            const value = time.format('HH:mm');
                            [
                              setEarliestTimeFrom,
                              setEarliestTimeTo,
                              setLatestTimeFrom,
                              setLatestTimeTo,
                            ][index](value);
                          }}
                        />
                      </div>
                    </div>
                  );
                })}

                {/** Contact Person */}
                <div className="col-sm-4">
                  <div className="custom-floating-group">
                    <input
                      type="text"
                      value={contactPerson}
                      onChange={(e) => setContactPerson(e.target.value)}
                    />
                    <label>{t('CONTACTPERSON')}</label>
                  </div>
                </div>

                {/** Contact Phone */}
                <div className="col-sm-4">
                  <div className="custom-floating-group">
                    <input
                      type="tel"
                      value={contactPhone}
                      onChange={(e) => setContactPhone(e.target.value)}
                    />
                    <label>{t('CONTACTPHONE')}</label>
                  </div>
                </div>

                <div className="col-sm-4">
                  <div className="custom-floating-group">
                    <input
                      type="text"
                      value={deliveryAddress}
                      onChange={(e) => setDeliveryAddress(e.target.value)}
                    />
                    <label>{t('DELIVERYADDRESS')}</label>
                  </div>
                </div>

                {/* Order Remark */}
                <div className="col-sm-4">
                  <div className="custom-floating-group">
                    <input
                      type="text"
                      value={orderRemark}
                      onChange={(e) => setOrderRemark(e.target.value)}
                    />
                    <label>{t('ORDERREMARK')}</label>
                  </div>
                </div>
              </div>
            </div>
            {/* Products Section */}
            <section className="products-section">
              <h5>{t('PRODUCTSINFO')}</h5>
              <button className="btn btn-success" onClick={addProduct}>
                {t('ADDPRODUCT')}
              </button>
              <div
                className={`table-outer ${products.length > 0 ? 'scroll' : ''}`}
              >
                <table className="table">
                  <thead>
                    <tr className="t-heading">
                      <th className="id">
                        <h5>#</h5>
                      </th>
                      <th className="code">
                        <h5>{t('PRODUCTCODE')}</h5>
                      </th>
                      <th className="name">
                        <h5>{t('PRODUCTNAME')}</h5>
                      </th>

                      <th className="quantity">
                        <h5>{t('QUANTITY')}</h5>
                      </th>

                      <th className="remark">
                        <h5>{t('PRODUCTREMARK')}</h5>
                      </th>

                      <th className="action">
                        {' '}
                        <h5>{t('ACTION')}</h5>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product, index) => (
                      <tr key={index}>
                        <td className="id">{index + 1}</td>
                        <td className="code">
                          <AsyncPaginate
                            // value={productInfo.find(
                            //   (option) =>
                            //     option.id === product.productId
                            // )}

                            value={
                              product.productId
                                ? {
                                    value: product.productId,
                                    label: `${product.productCode} - ${product.productName}`,
                                  }
                                : null
                            }
                            loadOptions={productsLoadOptions}
                            // onChange={(selectedOption) => {
                            //   const event = {
                            //     target: {
                            //       name: 'productId',
                            //       value: selectedOption?.value,
                            //       selectedOptions: [
                            //         {
                            //           getAttribute: () =>
                            //             selectedOption?.value || '',
                            //         },
                            //       ],
                            //     },
                            //   };
                            //   handleProductChange(index, event);
                            // }}
                            onChange={(selectedOption) =>
                              handleProductChange(index, selectedOption)
                            }
                            placeholder={t('SEARCHANDSELECTPRODUCT')}
                            // isDisabled={!product.isEditing}
                            isClearable
                            additional={{
                              page: 1,
                            }}
                          />
                        </td>
                        <td className="name">
                          <input
                            type="text"
                            name="productName"
                            value={product.productName}
                            onChange={(e) => handleProductChange(index, e)}
                            ref={(el) =>
                              (dynamicSizeInputRefs.current[index] = el)
                            }
                            disabled
                          />
                        </td>

                        <td className="quantity">
                          <input
                            type="number"
                            name="quantity"
                            value={trimDecimalZero(product.quantity)}
                            onChange={(e) => handleProductChange(index, e)}
                          />
                        </td>

                        <td className="remark">
                          <input
                            type="text"
                            name="productRemark"
                            value={product.productRemark}
                            onChange={(e) => handleProductChange(index, e)}
                            ref={(el) =>
                              (productRemarkInputRefs.current[index] = el)
                            }
                          />
                        </td>

                        <td className="action">
                          <div className="d-flex gap-1">
                            <button
                              className="btn btn-danger"
                              onClick={() => removeProduct(index)}
                            >
                              <img src="images/red-delete.svg" alt="" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
            <div className="bottom-button">
              <button
                className="cancel"
                onClick={() => setAddEditModal(false)}
                disabled={loader}
              >
                {t('CANCEL')}
              </button>
              <button
                className="save"
                onClick={() => handleSave()}
                disabled={loader}
              >
                {loader && (
                  <div className="spinner-border" role="status">
                    <span className="sr-only"></span>
                  </div>
                )}
                {t('SAVE')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogisticsOrderModal;
